import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import CyberLayout from '@/components/CyberLayout';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, ArrowRight, ArrowLeft, ExternalLink } from 'lucide-react';

export default function PrivacyConsent() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [accepted, setAccepted] = useState(false);

  const handleProceed = () => {
    if (accepted) {
      const timestamp = new Date().toISOString();
      sessionStorage.setItem('privacyAccepted', 'true');
      sessionStorage.setItem('privacyAcceptedAt', timestamp);
      navigate('/select-type');
    }
  };

  return (
    <CyberLayout>
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-20">
        <div className="max-w-2xl w-full">
          {/* Header */}
          <div className="text-center mb-10 fade-in-up">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-cyber-gray border border-neon-green/30 mb-6 neon-glow">
              <FileText className="w-10 h-10 text-neon-green" />
            </div>
            <h1 
              className="font-orbitron text-2xl sm:text-3xl font-bold text-white mb-2 tracking-wider"
              data-testid="privacy-title"
            >
              {t('privacyTitle')}
            </h1>
            <p className="font-mono text-neon-cyan text-sm tracking-widest uppercase">
              {t('privacySubtitle')}
            </p>
          </div>

          {/* Privacy Card */}
          <div className="cyber-card rounded-lg p-8 mb-6 fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="prose prose-invert max-w-none">
              <pre className="font-mono text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">
                {t('privacyText')}
              </pre>
            </div>
          </div>

          {/* Link to full privacy policy */}
          <div className="mb-8 fade-in-up" style={{ animationDelay: '0.15s' }}>
            <a
              href="https://www.smart-info.it/privacy"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="privacy-link"
              className="flex items-center justify-center gap-2 text-neon-cyan hover:text-neon-green transition-colors font-mono text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              <span>{t('privacyLink')}</span>
            </a>
          </div>

          {/* Checkbox */}
          <div 
            className="flex items-center gap-4 mb-8 cyber-card rounded-lg p-6 fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            <Checkbox
              id="privacy-accept"
              data-testid="privacy-checkbox"
              checked={accepted}
              onCheckedChange={setAccepted}
              className="w-6 h-6 border-2 border-neon-green data-[state=checked]:bg-neon-green data-[state=checked]:text-black"
            />
            <label 
              htmlFor="privacy-accept" 
              className="font-mono text-white cursor-pointer select-none"
            >
              {t('acceptPrivacy')}
            </label>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 fade-in-up" style={{ animationDelay: '0.3s' }}>
            <button
              onClick={() => navigate('/')}
              className="cyber-btn-outline px-6 py-3 flex items-center justify-center gap-2 rounded-md flex-1"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('back')}</span>
            </button>
            
            <button
              onClick={handleProceed}
              disabled={!accepted}
              data-testid="proceed-btn"
              className={`cyber-btn px-6 py-3 flex items-center justify-center gap-2 flex-1 ${
                !accepted ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <span>{t('proceedSecure')}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </CyberLayout>
  );
}
